import React, { createContext, useContext, useEffect, useState } from 'react';

interface StockContextType {
  getStock: (productId: string, variantId?: string) => number;
  isMadeToOrder: (productId: string, variantId?: string) => boolean;
  loading: boolean;
  error: string | null;
}

const StockContext = createContext<StockContextType | null>(null);

export const useStock = () => {
  const context = useContext(StockContext);
  if (!context) {
    throw new Error('useStock must be used within a StockProvider');
  }
  return context;
};

export const StockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [inventory, setInventory] = useState<Record<string, number>>({});
  const [madeToOrderMap, setMadeToOrderMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        setLoading(true);

        const response = await fetch('/api/data?type=stock');

        if (!response.ok) {
          throw new Error(`Failed to fetch inventory: ${response.statusText}`);
        }

        const data = await response.json();
        const inventoryMap: Record<string, number> = {};
        const onOrderMap: Record<string, boolean> = {};

        // API returns flat array with id, stockCount, madeToOrder
        (data.inventory || []).forEach((item: { id: string; stockCount: number; madeToOrder: boolean }) => {
          inventoryMap[item.id] = item.stockCount || 0;
          onOrderMap[item.id] = item.madeToOrder || false;
        });

        setInventory(inventoryMap);
        setMadeToOrderMap(onOrderMap);
        setError(null);
      } catch (err) {
        console.error('Error fetching inventory:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const getStock = (productId: string, variantId?: string): number => {
    // While loading, assume items are in stock
    if (loading) {
      return 999;
    }

    // If variant is specified, look up by variant ID
    if (variantId) {
      return inventory[variantId] || 0;
    }
    // Otherwise, look up by product ID
    return inventory[productId] || 0;
  };

  const isMadeToOrder = (productId: string, variantId?: string): boolean => {
    // While loading, assume not made-to-order
    if (loading) {
      return false;
    }

    // If variant is specified, look up by variant ID
    if (variantId) {
      return madeToOrderMap[variantId] || false;
    }
    // Otherwise, look up by product ID
    return madeToOrderMap[productId] || false;
  };

  return (
    <StockContext.Provider value={{ getStock, isMadeToOrder, loading, error }}>
      {children}
    </StockContext.Provider>
  );
};

export default StockContext;
